import Grid2 from "@mui/material/Grid2";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha, styled } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";

import { MASCOT_PALETTE } from "@/components/Others/MascotPanel";
import { colors } from "@/theme";

import { accentColor, borderColor, headerBg, isDarkTheme, surfaceBg } from "../../utils/matchOdds";
import { headerFocusRing } from "./shared";

/* The match lists: sport and league headers, a match row, its score and its cells. */

/** How many matches a sport has, on the tile the In-Play strip gives a count. */
export const SportCount = styled("span")(({ theme }) => ({
  minWidth: 24,
  padding: "0 8px",
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 700,
  lineHeight: "22px",
  textAlign: "center",
  fontVariantNumeric: "tabular-nums",
  color: MASCOT_PALETTE.deepInk,
  backgroundColor: alpha(colors.prime, 0.12),
  boxShadow: `inset 0 0 0 1px ${alpha(colors.prime, 0.28)}`,
  ...theme.applyStyles("dark", {
    color: MASCOT_PALETTE.sky,
    backgroundColor: alpha(MASCOT_PALETTE.sky, 0.12),
    boxShadow: `inset 0 0 0 1px ${alpha(MASCOT_PALETTE.sky, 0.32)}`,
  }),
}));

/** The sport's icon on its header, in a tile of the mascot's sky. */
export const SportIconTile = styled("span")(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 28,
  height: 28,
  flexShrink: 0,
  borderRadius: 8,
  color: MASCOT_PALETTE.deepInk,
  backgroundColor: alpha(colors.prime, 0.12),
  ...theme.applyStyles("dark", {
    color: MASCOT_PALETTE.sky,
    backgroundColor: alpha(MASCOT_PALETTE.sky, 0.14),
  }),
}));

/**
 * A sport's header: lit from the left with the blue the mascot stands in,
 * its name in the sky blue on the night and the deep ink on the light page.
 */
export const SportHeader = styled(Stack)(({ theme }) => ({
  padding: "10px 12px",
  cursor: "pointer",
  userSelect: "none",
  color: MASCOT_PALETTE.deepInk,
  borderBottom: `1px solid ${borderColor(theme)}`,
  background: `linear-gradient(90deg, ${alpha(colors.prime, 0.14)}, ${alpha(colors.prime, 0)} 60%), ${colors.bgLight}`,
  ...theme.applyStyles("dark", {
    color: MASCOT_PALETTE.sky,
    background: `linear-gradient(90deg, ${alpha(MASCOT_PALETTE.sky, 0.16)}, ${alpha(MASCOT_PALETTE.sky, 0)} 60%), ${colors.layer2Dark}`,
  }),
  ...headerFocusRing(theme),
}));

/**
 * A league's header. On a card of its own (a sport's page) it is the accent
 * on the page colour; inside a sport's card it is the page ink on the card,
 * a line under the sport rather than a second accent.
 */
export const LeagueHeader = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "nested",
})<{ nested?: boolean }>(({ theme, nested }) => ({
  paddingLeft: 12,
  paddingRight: 12,
  paddingTop: 8,
  paddingBottom: 8,
  cursor: "pointer",
  userSelect: "none",
  color: nested ? theme.palette.text.primary : accentColor(theme),
  background: nested ? surfaceBg(theme) : headerBg(theme),
  transition: "background-color .15s ease",
  "@media (hover: hover)": {
    "&:hover": { backgroundColor: alpha(colors.prime, isDarkTheme(theme) ? 0.07 : 0.04) },
  },
  ...headerFocusRing(theme),
}));

export const SubHeader = styled(Grid2)(({ theme }) => ({
  paddingLeft: 12,
  paddingRight: 12,
  paddingTop: 4,
  paddingBottom: 4,
  borderTop: `1px solid ${borderColor(theme)}`,
}));

export const CenteredGrid = styled(Grid2)({
  display: "flex",
  justifyContent: "center",
});

export const ColumnLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "flexValue",
})<{ flexValue: number }>(({ flexValue }) => ({
  flex: flexValue,
  textAlign: "center",
  fontWeight: 600,
}));

/** One match. Lit a little under the pointer, so the row reads as one thing. */
export const RowContainer = styled(Grid2)(({ theme }) => ({
  paddingLeft: 12,
  paddingRight: 12,
  paddingTop: 10,
  paddingBottom: 10,
  borderTop: `1px solid ${borderColor(theme)}`,
  transition: "background-color .15s ease",
  "@media (hover: hover)": {
    "&:hover": { backgroundColor: alpha(colors.prime, isDarkTheme(theme) ? 0.07 : 0.04) },
  },
}));

/**
 * The two sides with the clock under them, as the link to the match that
 * they are: the whole block takes the pointer, a middle click opens the match
 * in a new tab, and the keyboard reaches it. Takes what room the row gives it.
 */
export const SidesLink = styled(RouterLink)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: 4,
  color: "inherit",
  textDecoration: "none",
  borderRadius: 6,
  "@media (hover: hover)": {
    "&:hover .team-name": { color: accentColor(theme) },
  },
  "&:focus-visible": {
    outline: `2px solid ${isDarkTheme(theme) ? MASCOT_PALETTE.sky : accentColor(theme)}`,
    outlineOffset: 2,
  },
}));

export const TeamName = styled(Typography)(({ theme }) => ({
  color: isDarkTheme(theme) ? colors.white : colors.dark,
  fontWeight: 500,
  transition: "color .15s ease",
}));

/** The score, one box per side, lined up with the side it belongs to. */
export const ScoreColumn = styled(Stack)({
  flexShrink: 0,
  alignSelf: "flex-start",
  gap: 4,
});

/**
 * One side's score, the height of the side's line so the two read as a
 * scoreboard. The side in front wears the crown gold, the mascot's colour for
 * the one that is winning, on a deep gold ink in the light scheme where the
 * gold itself is too pale to read; the other sits in the sky blue.
 */
export const ScoreBox = styled("span", {
  shouldForwardProp: (prop) => prop !== "leading",
})<{ leading: boolean }>(({ theme, leading }) => {
  const dark = isDarkTheme(theme);
  const accent = accentColor(theme);

  return {
    minWidth: 30,
    padding: "0 6px",
    borderRadius: 6,
    textAlign: "center" as const,
    fontWeight: 700,
    fontSize: 13,
    lineHeight: "24px",
    fontVariantNumeric: "tabular-nums",
    color: dark ? MASCOT_PALETTE.sky : accent,
    backgroundColor: dark ? alpha(MASCOT_PALETTE.sky, 0.12) : alpha(colors.prime, 0.1),
    border: `1px solid ${dark ? alpha(MASCOT_PALETTE.sky, 0.4) : alpha(accent, 0.45)}`,
    ...(leading && {
      color: dark ? MASCOT_PALETTE.crown : "#6B4A00",
      backgroundColor: alpha(MASCOT_PALETTE.crown, dark ? 0.16 : 0.3),
      borderColor: dark ? alpha(MASCOT_PALETTE.crown, 0.8) : "#C9971B",
    }),
  };
});

/** "+12": the markets beyond the main ones, as the link to them that it is. */
export const MarketCountBadge = styled(RouterLink)(({ theme }) => {
  const accent = accentColor(theme);
  const tint = alpha(colors.prime, isDarkTheme(theme) ? 0.14 : 0.1);

  return {
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    textDecoration: "none",
    minWidth: 40,
    minHeight: 24,
    padding: "2px 10px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    lineHeight: "18px",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
    color: accent,
    background: surfaceBg(theme),
    border: `1px solid ${borderColor(theme)}`,
    transition: "border-color .2s ease, background-color .2s ease",
    "@media (hover: hover)": {
      "&:hover": { borderColor: accent, background: tint },
    },
    "&:focus-visible": { outline: `2px solid ${accent}`, outlineOffset: 2 },
    // The pill stays small; a thumb gets a 44 px target around it.
    position: "relative",
    "@media (pointer: coarse)": {
      "&::after": { content: '""', position: "absolute", inset: "-10px -4px" },
    },
  };
});

export const LockedMarketCell = styled(Grid2)(({ theme }) => ({
  minHeight: 40,
  "@media (pointer: coarse)": { minHeight: 44 },
  gap: 6,
  fontSize: 12,
  fontWeight: 600,
  paddingTop: 8,
  paddingBottom: 8,
  borderRadius: "8px",
  background: headerBg(theme),
  border: `1px solid ${borderColor(theme)}`,
  // Secondary, not disabled: the cell now says "Suspended" and has to be readable.
  color: theme.palette.text.secondary,
}));

/** The set in play and the game's points next to the match score on a list row; quieter than the score itself. */
export const SubScoreBox = styled("span")(({ theme }) => ({
  minWidth: 26,
  padding: "0 4px",
  borderRadius: 6,
  textAlign: "center" as const,
  fontWeight: 600,
  fontSize: 12,
  lineHeight: "24px",
  fontVariantNumeric: "tabular-nums",
  color: theme.palette.text.secondary,
  border: `1px solid ${borderColor(theme)}`,
}));

/**
 * The part of a folding header that is the button: a real `<button>`, so a
 * follow star or another control can sit beside it rather than inside it.
 */
export const SectionToggle = styled("button")(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  display: "flex",
  alignItems: "center",
  gap: 8,
  margin: 0,
  padding: 0,
  border: 0,
  borderRadius: 6,
  background: "none",
  color: "inherit",
  font: "inherit",
  textAlign: "left",
  cursor: "pointer",
  "&:focus-visible": {
    outline: `2px solid ${isDarkTheme(theme) ? MASCOT_PALETTE.sky : accentColor(theme)}`,
    outlineOffset: 4,
  },
}));

/** The fold's chevron: turns with the section, and holds still under reduced motion. */
export const SectionChevron = styled("span", {
  shouldForwardProp: (prop) => prop !== "open",
})<{ open: boolean }>(({ open }) => ({
  display: "inline-flex",
  flexShrink: 0,
  transition: "transform .15s ease",
  transform: open ? "rotate(180deg)" : "none",
  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
}));
